import React, { ReactNode } from "react";
import { IWebsiteBuilderGridCell, websiteBuilderGenerateGridCellDesignStyles, websiteBuilderGridCellFilled } from "../../../../nodes/gridCell/websiteBuilderGridCell";
import { useWebsiteBuilderEditorContext } from "../../../../websiteBuilderEditorContext";
import { useActivePageDomNodeRefMapRegistration } from "../../../../hooks/activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { TWebsiteBuilderDraggableNodeDropItemType } from "../../../../nodes/websiteBuilderNode";
import { useDrop } from "react-dnd";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { IWebsiteBuilderGrid } from "../../../../nodes/container/websiteBuilderGrid";
import { TWebsiteBuilderSection } from "../../../../nodes/section/websiteBuilderSection";
import { websiteBuilderInsertElementIntoGridCell } from "../../../../hooks/nodeOperations/helpers/sectionEntityOperations";
import classNames from "classnames";
import { isWebsiteBuilderStretchElementData, TWebsiteBuilderElement } from "../../../../nodes/element/websiteBuilderElement";

interface IWebsiteBuilderGridCellViewProps {
  node: IWebsiteBuilderGridCell;
  gridNode: IWebsiteBuilderGrid;
  sectionNode: TWebsiteBuilderSection;
  children: ReactNode;
}

interface IWebsiteBuilderGridCellViewDropState {
  gridCellIsDropTarget: boolean;
}

const ACCEPTED_GRID_CELL_DROP_ITEMS: TWebsiteBuilderDraggableNodeDropItemType[] = ["website-builder-element"];

export function WebsiteBuilderGridCellView(props: IWebsiteBuilderGridCellViewProps) {
  const { node, gridNode, sectionNode, children } = props;

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);

  const { activePageDomNodeRefMap } = useWebsiteBuilderEditorContext();

  const activePageDomNodeRefMapRegistration = useActivePageDomNodeRefMapRegistration(node.id, activePageDomNodeRefMap);

  const designStyles = websiteBuilderGenerateGridCellDesignStyles();

  function getGridCellIsMoveTarget() {
    if (
      selectedNode?.editingMode?.type === "element-move" &&
      selectedNode.editingMode.gridCellMoveTargetId === node.id
    ) {
      if (!websiteBuilderGridCellFilled(node)) {
        return true;
      } else {
        if (node.children[0].id !== selectedNode.id && gridNode.children.some(child => websiteBuilderGridCellFilled(child) && child.children[0].id === selectedNode.id)) {
          return true;
        } else {
          return false;
        }
      }
    } else {
      return false;
    }
  }

  const gridCellIsMoveTarget = getGridCellIsMoveTarget();

  const [{ gridCellIsDropTarget }, drop] = useDrop<TWebsiteBuilderElement, unknown, IWebsiteBuilderGridCellViewDropState>(
    () => ({
      accept: ACCEPTED_GRID_CELL_DROP_ITEMS,
      drop: (item, monitor) => {
        if (monitor.didDrop()) {
          return;
        }

        websiteBuilderInsertElementIntoGridCell(item, node, gridNode, sectionNode, activePageId, deviceView, dispatch);
      },
      canDrop: item => {
        return (
          !websiteBuilderGridCellFilled(node) &&
          item.type === "element" && (
            !isWebsiteBuilderStretchElementData(item.element) ||
            !item.element.stretch.enabled
          )
        );
      },
      collect: (monitor): IWebsiteBuilderGridCellViewDropState => {
        return {
          gridCellIsDropTarget: monitor.canDrop() && monitor.isOver(),
        };
      },
    }),
    [
      node,
      gridNode,
      sectionNode,
      activePageId,
      deviceView,
    ],
  );

  function getGridCellHighlight() {
    return gridCellIsDropTarget || gridCellIsMoveTarget;
  }

  return (
    <div
      ref={el => {
        drop(el);
        activePageDomNodeRefMapRegistration(el);
      }}
      className={classNames(designStyles.className, "editor-grid-cell", {
        "editor-grid-cell-highlight": getGridCellHighlight(),
      })}
      style={designStyles.styleProperties}
    >
      {children}
    </div>
  );
}